import React, { useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import StaffProfileForm from "../../components/profile/StaffProfileForm";
import CareerTimeline from "../../components/profile/career-timeline/CareerTimeline";
import { userService } from "../../services/api";
import { primaryRoleType, isCoachingRole } from "../../constants/staffRoles";

// The payload speaks snake_case; the form state speaks camelCase. This says
// which form key each payload field is BUILT FROM, so "has it changed?" compares
// a value against its own source instead of against a key that does not exist.
//
// Without it the check read `initialFormData["playing_styles"]` — undefined,
// always — so every field below counted as changed on every single save. For
// most of them that only meant a needlessly fat payload. For `playing_styles`
// and `defensive_tactics` it meant destruction: this page has no input for them,
// so the value was an empty list, and the API takes an empty list literally.
// A staff member who corrected his height lost his tactics, silently.
const PAYLOAD_FIELD_SOURCE = {
  staff_type: "staff_type",
  role_title_custom: "role_title_custom",
  works_for_club: "works_for_club",
  address: "address",
  city: "city",
  country: "country",
  zipcode: "zipcode",
  height: "height",
  weight: "weight",
  date_of_birth: "birthday",
  languages: "languages",
  playing_styles: "playingStyles",
  defensive_tactics: "defensiveTactics",
  coaching_notes: "coachingPhilosophy",
  sportmonks_coach: "sportmonks_coach",
  claim_representation: "claim_representation",
  technical_skills_video_analysis: "videoAnalysis",
  technical_skills_gps_performance: "gpsPerformance",
  technical_skills_software: "software",
  specializations: "specializations",
  nationality: "nationality",
  passports_held: "passportHeld",
  work_permits: "workPermits",
  contract_status: "contractStatus",
  current_organization: "currentClub",
  contract_expiry_date: "contractExpiryDate",
  willingness_to_relocate: "willingnessToRelocate",
  preferred_regions: "preferredRegions",
  website_url: "website",
  linkedin_url: "linkedin",
  social_urls: "social",
};

// `role_specific` is ONE stored dictionary, replaced whole on write. It is built
// from the career-timeline state, so it has to be built from the untouched state
// too — otherwise there is nothing to compare it against and it goes out on
// every save, taking whatever it does not happen to know with it.
const buildRoleSpecific = (formSource, careerSource) => {
  const roleSpecific = {};
  if (!careerSource) return roleSpecific;

  // Reduce any title (existing or new) to its canonical role for branch matching
  const position = primaryRoleType(formSource?.position || careerSource.position);

  if (position === "assistant_coach") {
    roleSpecific.player_developed = (careerSource.contributions || []).map((contrib) => contrib.text);
  }

  if (position === "goalkeeper_coach") {
    roleSpecific.notable_goalkeepers_trained = careerSource.notableGkTrained?.join(", ") || "";
    roleSpecific.methodology = careerSource.gkMethodology || "";
  }

  if (position === "technical_director") {
    roleSpecific.projects_programs_led = careerSource.projectsProgramsLed || "";
    roleSpecific.staff_supervised = careerSource.staffSupervised || "";
  }

  if (position === "sporting_director") {
    roleSpecific.key_transfers_managed = careerSource.keyTransfersManaged || "";
    roleSpecific.partnerships_networks = careerSource.partnershipsNetworks || "";
  }

  if (position === "head_coach") {
    roleSpecific.tournament_travel_organization = careerSource.tournamentTravelOrg || "";
    roleSpecific.administrative_experience = careerSource.administrativeExperience || "";
  }

  if (position === "scout") {
    roleSpecific.successful_players_discovered = careerSource.successfulPlayersDiscovered?.join(", ") || "";
  }

  if (position === "physiotherapist") {
    roleSpecific.teams_players_treated = careerSource.teamsPlayersTreated?.join(", ") || "";
  }

  if (position === "nutritionist") {
    roleSpecific.teams_players_supported = careerSource.teamsPlayersSupported?.join(", ") || "";
  }

  if (position === "psychologist") {
    roleSpecific.focus_areas = careerSource.psychologistFocusAreas || [];
    roleSpecific.players_teams_supported = careerSource.psychologistSupported?.join(", ") || "";
  }

  if (position === "performance_analyst") {
    roleSpecific.analysis_types = careerSource.analysisTypes || [];
  }

  if (position === "youth_coordinator") {
    roleSpecific.programs_developed = careerSource.programsDeveloped || "";
    roleSpecific.talents_promoted = careerSource.talentsPromoted || "";
    roleSpecific.academy_structures_built = careerSource.academyStructuresBuilt || "";
  }

  return roleSpecific;
};

const StaffProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Get active tab from URL or default to account-information for staff
  const activeTab = searchParams.get("tab") || "account-information";

  // Staff profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  // Per-field save errors (e.g. an implausible date of birth) so the message
  // lands on the field that caused it, not only in the banner.
  const [fieldErrors, setFieldErrors] = useState({});

  // Form data state - will be managed by child components but saved here
  const [formData, setFormData] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);

  // Career timeline data state
  const [careerData, setCareerData] = useState({
    experience: [],
    achievements: [],
    honors: [],
    contributions: [],
    publications: [],
    references: [],
    // NOTE: `additionalNotes` deliberately does NOT live here. It is the same
    // stored field as the coaching philosophy (`coaching_notes`), and that field
    // has exactly one owner: `formData.coachingPhilosophy`. Keeping a second
    // copy here is what used to make one of the two boxes win on save.
    position: "", // Add position to career data
    // role-driven fields defaults for page 2
    notableGkTrained: [],
    gkMethodology: "",
    teamsPlayersSupported: [],
    projectsProgramsLed: "",
    staffSupervised: "",
    keyTransfersManaged: "",
    partnershipsNetworks: "",
    tournamentTravelOrg: "",
    administrativeExperience: "",
    successfulPlayersDiscovered: [],
    teamsPlayersTreated: [],
    psychologistFocusAreas: [],
    psychologistSupported: [],
    analysisTypes: [],
    reportsPresentations: [],
    programsDeveloped: "",
    talentsPromoted: "",
    academyStructuresBuilt: "",
    academyDocs: [],
  });
  const [initialCareerData, setInitialCareerData] = useState(null);

  // Update URL when tab changes
  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  // Handle edit/save/cancel actions
  const handleEdit = () => {
    setIsEditing(true);
    // Store initial data when editing starts
    if (formData && !initialFormData) {
      setInitialFormData({ ...formData });
    }
    if (careerData && !initialCareerData) {
      setInitialCareerData({ ...careerData });
    }
  };

  // Compare two values to detect changes. Lists and dictionaries are compared by
  // content — comparing them by identity would call every one of them changed,
  // which is how empty values used to travel with every save.
  const hasChanged = (newVal, oldVal) => {
    if (typeof newVal === "object" && newVal !== null && typeof oldVal === "object" && oldVal !== null) {
      return JSON.stringify(newVal) !== JSON.stringify(oldVal);
    }
    return newVal !== oldVal;
  };

  // Build combined delta payload from both form and career data
  const buildCombinedDelta = useCallback((formData, careerData, initialFormData, initialCareerData) => {
    const delta = {};

    if (!formData || !careerData) return delta;

    // Profile form fields (only include allowed fields according to MD strict field list)
    const allowedFields = {
      staff_type: formData.staff_type || "coach",
      role_title_custom: formData.role_title_custom || null,
      works_for_club: formData.works_for_club || true,
      address: formData.address || "",
      city: formData.city || "",
      country: formData.country || "",
      zipcode: formData.zipcode || "",
      height: formData.height ? formData.height.toString() : "",
      weight: formData.weight ? formData.weight.toString() : "",
      date_of_birth: formData.birthday || null,
      languages: (formData.languages || []).map((lang) => lang.language),
      playing_styles: formData.playingStyles || [],
      defensive_tactics: formData.defensiveTactics || [],
      // One field, one source. This used to read
      //   careerData.additionalNotes || formData.coachingPhilosophy
      // which let the career-timeline notes box beat the coaching philosophy
      // box: fill in both and the philosophy vanished on save without a word.
      coaching_notes: formData.coachingPhilosophy || "",
      sportmonks_coach: formData.sportmonks_coach || null,
      claim_representation: formData.claim_representation || false,
      
      // Technical Skills (updated field names for new API)
      technical_skills_video_analysis: formData.videoAnalysis || [],
      technical_skills_gps_performance: formData.gpsPerformance || [],
      technical_skills_software: formData.software || [],
      specializations: formData.specializations || [],

      // Work/Availability fields (updated field names)
      nationality: formData.nationality || "",
      passports_held: formData.passportHeld || [],
      work_permits: formData.workPermits || [],
      contract_status: formData.contractStatus || "",
      current_organization: formData.currentClub || "",
      contract_expiry_date: formData.contractExpiryDate || null,
      willingness_to_relocate: formData.willingnessToRelocate === "yes" || formData.willingnessToRelocate === true,
      preferred_regions: formData.preferredRegions || [],

      // Link fields (updated field names)
      website_url: formData.website || "",
      linkedin_url: formData.linkedin || "",
      social_urls: formData.social ? [formData.social] : [],

      // Goalkeeping licenses will be populated below from licenses with goalkeeping category
    };

    // Add career timeline fields
    if (careerData.experience && hasChanged(careerData.experience, initialCareerData?.experience)) {
      allowedFields.experience = (careerData.experience || []).map((exp) => ({
        club: exp.club,
        country: exp.country,
        position: exp.position || exp.role,
        start: exp.from,
        end: exp.to === "present" ? null : exp.to,
        type: exp.type || "full_time",
      }));
    }

    if (careerData.achievements && hasChanged(careerData.achievements, initialCareerData?.achievements)) {
      allowedFields.trophies = (careerData.achievements || []).map((achievement) => ({
        title: achievement.title,
        club: achievement.club,
        season: achievement.year,
      }));
    }

    if (formData.licenses && hasChanged(formData.licenses, initialFormData?.licenses)) {
      // Separate goalkeeping licenses from regular licenses
      const regularLicenses = [];
      const gkLicenses = [];
      
      (formData.licenses || []).forEach((license) => {
        const licenseData = {
          title: license.type,
          authority: license.licenseId,
          year: parseInt(license.issueDate) || new Date().getFullYear(),
        };
        
        // Check if it's a goalkeeping license based on type
        if (license.type && license.type.toLowerCase().includes('gk') || 
            license.type && license.type.toLowerCase().includes('goalkeeper') ||
            license.type && license.type.toLowerCase().includes('goalkeeping')) {
          gkLicenses.push(license.type);
        } else {
          regularLicenses.push(licenseData);
        }
      });
      
      allowedFields.licenses = regularLicenses;
      allowedFields.gk_licenses = gkLicenses;
    }

    if (careerData.honors && hasChanged(careerData.honors, initialCareerData?.honors)) {
      allowedFields.honors_and_awards = (careerData.honors || []).map((honor) => ({
        title: honor.title,
        year: parseInt(honor.year) || new Date().getFullYear(),
        issuer: honor.issuer || "",
      }));
    }

    if (careerData.contributions && hasChanged(careerData.contributions, initialCareerData?.contributions)) {
      allowedFields.notable_contributions = (careerData.contributions || []).map(
        (contrib) => contrib.text
      );
    }

    if (formData.managementResponsibilities && hasChanged(formData.managementResponsibilities, initialFormData?.managementResponsibilities)) {
      allowedFields.management_responsibilities = formData.managementResponsibilities || [];
    }

    if (careerData.publications && hasChanged(careerData.publications, initialCareerData?.publications)) {
      allowedFields.publications = (careerData.publications || []).map((pub) => ({
        title: pub.title,
        link: pub.link,
      }));
    }

    if (careerData.references && hasChanged(careerData.references, initialCareerData?.references)) {
      allowedFields.references = (careerData.references || []).map((ref) => ({
        name: ref.name,
        phone_number: ref.phone,
        email: ref.email,
        note: ref.note || "",
      }));
      
      allowedFields.reference_contacts_public =
        careerData.references?.length > 0
          ? careerData.references[0]?.isVisible || false
          : false;
    }

    // Role-specific data — one dictionary, replaced whole on write, so it only
    // goes out when the state it is built from actually moved.
    const roleSpecific = buildRoleSpecific(formData, careerData);
    const initialRoleSpecific = buildRoleSpecific(
      initialFormData || formData,
      initialCareerData
    );

    if (
      Object.keys(roleSpecific).length > 0 &&
      hasChanged(roleSpecific, initialRoleSpecific)
    ) {
      allowedFields.role_specific = roleSpecific;
    }

    // Only include fields that have actually changed. The comparison runs on the
    // FORM value each payload field is built from, not on the payload key: the
    // payload is snake_case, the form state is camelCase, and looking a
    // snake_case key up in camelCase state finds nothing and calls it a change.
    // Fields added by the blocks above carry their own comparison already and
    // pass straight through.
    Object.keys(allowedFields).forEach((key) => {
      const sourceKey = PAYLOAD_FIELD_SOURCE[key];
      if (!sourceKey) {
        delta[key] = allowedFields[key];
        return;
      }
      if (hasChanged(formData[sourceKey], initialFormData?.[sourceKey])) {
        delta[key] = allowedFields[key];
      }
    });

    return delta;
  }, []);

  // Centralized save function - only called when save button is clicked
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      // Build combined delta payload
      const apiData = buildCombinedDelta(formData, careerData, initialFormData, initialCareerData);

      console.log("Sending combined delta payload:", apiData);

      // If no changes detected, don't make API call
      if (Object.keys(apiData).length === 0) {
        console.log("No changes detected, skipping API call");
        setIsEditing(false);
        setIsSaving(false);
        return;
      }

      // Make single API call with all data
      const response = await userService.updateStaffProfile(apiData);

      if (response.success) {
        // Update initial data to reflect saved state
        setInitialFormData(formData ? { ...formData } : null);
        setInitialCareerData(careerData ? { ...careerData } : null);
      setIsEditing(false);
        console.log("Profile updated successfully");
      } else {
        if (response.fieldErrors) {
          setFieldErrors(response.fieldErrors);
        }
        throw new Error(response.error || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      setError(error.message || "Failed to save profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Revert both form and career data to initial state
    if (initialFormData) {
      setFormData({ ...initialFormData });
    }
    if (initialCareerData) {
      setCareerData({ ...initialCareerData });
    }
    setError(null);
    setFieldErrors({});
    setIsEditing(false);
  };



  const tabs = [
    { id: "account-information", label: "Account Information" },
    { id: "career-timeline", label: "Career Timeline" }
  ];

  // `coaching_notes` is ONE stored field. A coaching role edits it as the
  // COACHING PHILOSOPHY on the account tab; everyone else edits it as ADDITIONAL
  // NOTES on the career timeline. Never both — two boxes on one field is exactly
  // how text used to disappear on save.
  const coachingNotes = formData?.coachingPhilosophy ?? "";
  const staffIsCoach = isCoachingRole(formData?.position);

  // The timeline reads the one value instead of holding a second copy of it,
  // so what is stored is actually visible there rather than an empty box the
  // next save would overwrite.
  const careerTimelineData = useMemo(
    () => ({ ...careerData, additionalNotes: coachingNotes }),
    [careerData, coachingNotes]
  );

  const handleUpdateCareerData = useCallback(
    (next) => {
      const { additionalNotes, ...rest } = next || {};
      if (additionalNotes !== undefined && additionalNotes !== coachingNotes) {
        setFormData((prev) => ({ ...(prev || {}), coachingPhilosophy: additionalNotes }));
      }
      setCareerData(rest);
    },
    [coachingNotes]
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "account-information":
        return (
          <StaffProfileForm 
            isEditing={isEditing}
            isSaving={isSaving}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            formData={formData}
            setFormData={setFormData}
            fieldErrors={fieldErrors}
            disableAutoSave={true} // Disable automatic save hooks
          />
        );
      case "career-timeline":
        return (
          <CareerTimeline
            isEditing={isEditing}
            isSaving={isSaving}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            careerData={careerTimelineData}
            onUpdateCareerData={handleUpdateCareerData}
            // A coaching role already edits this text as the coaching philosophy
            // on the account tab, so it must not appear a second time here.
            showAdditionalNotes={!staffIsCoach}
            disableAutoSave={true} // Disable automatic save hooks
          />
        );
      default:
        return (
          <StaffProfileForm 
            isEditing={isEditing}
            isSaving={isSaving}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
            formData={formData}
            setFormData={setFormData}
            fieldErrors={fieldErrors}
            disableAutoSave={true}
          />
        );
    }
  };

  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white py-4 border-b border-[#E5E6E8]">
          <div className="px-4 md:px-14 flex items-center justify-between">
            <h1 className="text-[20px] font-bold text-gray-900">My Profile</h1>
            <button onClick={() => navigate(`/search/staff/${formData.account_id}`)} className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg">
              Preview public profile
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-4 md:px-14 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setError(null)}
                      className="bg-red-100 px-2 py-1.5 rounded-md text-red-800 hover:bg-red-200 text-sm font-medium"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        {isMobile ? (
          <div className="px-4 bg-white flex justify-between items-center border-y border-[#0000001A] overflow-x-auto">
            <div className="flex items-center gap-4">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 text-xs whitespace-nowrap cursor-pointer ${
                    activeTab === tab.id
                      ? "border-b-2 border-[#2283FF] text-[#2283FF] font-medium"
                      : "text-[#1E1E1ECC]"
                  }`}
                >
                  {tab.label}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white py-4 flex items-center">
            <div className="px-14">
              <nav className="flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-4 font-medium text-sm rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? "bg-[#F3F4F6] text-[#0B0B0B]"
                        : "text-[#1E1E1ECC] hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}


        {/* Tab Content */}
        <div className="bg-[#F8F9FB] rounded-lg shadow-sm">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default StaffProfile;
